/**
 * useAutoSync Composable
 *
 * Automatically triggers sync when the app comes back online,
 * plus a periodic retry timer for failed items.
 *
 * Also refreshes read caches (Courses, Sessions) after reconnect
 * when the data is older than 30 minutes.
 */

import { onMounted, onUnmounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useAttendancesStore } from '@/stores/attendances.store'
import { useGuestsStore } from '@/stores/guests.store'
import { useSessionsStore } from '@/stores/sessions.store'
import { useCoursesStore } from '@/stores/courses.store'
import { usePhotosStore } from '@/stores/photos.store'
import { db } from '@/db'

const CACHE_STALE_MS = 30 * 60_000 // 30 minutes

export function useAutoSync() {
  const attendancesStore = useAttendancesStore()
  const guestsStore = useGuestsStore()
  const sessionsStore = useSessionsStore()
  const coursesStore = useCoursesStore()
  const photosStore = usePhotosStore()
  const toast = useToast()

  let retryTimer: number | null = null
  let isSyncing = false // Guard against parallel sync runs

  /**
   * Refresh read-only caches in the background after reconnect.
   * Sessions are ALWAYS refreshed (isBilled may have changed).
   * Courses only when stale (> 30 min).
   * Fire-and-forget — no toast, no error propagation.
   */
  async function runCacheRefresh(): Promise<void> {
    if (!navigator.onLine) return

    try {
      // Refresh courses if stale
      const coursesStale =
        !coursesStore.lastFetched ||
        Date.now() - coursesStore.lastFetched.getTime() > CACHE_STALE_MS
      if (coursesStale) {
        await coursesStore.fetchAll(true)
      }

      // Always refresh sessions — isBilled can change at any time
      // (e.g. QA submitted/reverted on another device)
      sessionsStore.invalidateCache()
      const courseIds = coursesStore.courses.map((c) => c.id)
      await Promise.all(courseIds.map((id) => sessionsStore.fetchByCourse(id, true)))
    } catch {
      // Fire-and-forget — cache refresh failures are silent
    }
  }

  /**
   * @param refreshCaches - When true, refresh read caches after sync.
   *   Only set on reconnect (online event), NOT on the 30s periodic timer.
   */
  async function runSync(refreshCaches = false) {
    if (isSyncing || !navigator.onLine) return
    const pending = await db.getPendingCount()
    if (pending === 0) {
      if (refreshCaches) runCacheRefresh().catch(() => {})
      return
    }

    isSyncing = true
    try {
      const [a, g, s] = await Promise.all([
        attendancesStore.syncPending(),
        guestsStore.syncPending(),
        sessionsStore.syncPendingInstructors(),
      ])

      // Photo uploads (sequential — large payloads, not in Promise.all)
      const p = await photosStore.syncQueue()

      const success = a.success + g.success + s.success + p.success
      const failed = a.failed + g.failed + s.failed + p.failed

      if (success > 0) {
        toast.add({
          severity: 'success',
          summary: 'Synchronisiert',
          detail: `${success} Änderung${success !== 1 ? 'en' : ''} synchronisiert`,
          life: 12000,
        })
      }
      if (failed > 0) {
        toast.add({
          severity: 'warn',
          summary: 'Sync-Fehler',
          detail: `${failed} Änderung${failed !== 1 ? 'en' : ''} konnten nicht synchronisiert werden`,
          life: 20000,
        })
      }
      // Endgültig abgelehnt: wird nicht wiederholt, also genau einmal deutlich melden
      if (a.rejected.length > 0) {
        toast.add({
          severity: 'error',
          summary: 'Anwesenheit nicht gespeichert',
          detail: a.rejected.join('\n'),
          life: 30000,
        })
      }
    } finally {
      isSyncing = false
      // After sync, refresh caches in the background (only on reconnect)
      if (refreshCaches) runCacheRefresh().catch(() => {})
    }
  }

  onMounted(() => {
    // Listen directly to the native browser event — avoids creating a second
    // useOffline() instance which would double-fire 'app:back-online'
    const handleOnline = () => runSync(true)
    window.addEventListener('online', handleOnline)
    // Periodic retry every 30s for items that failed on first attempt
    retryTimer = window.setInterval(runSync, 30_000)

    onUnmounted(() => {
      window.removeEventListener('online', handleOnline)
      if (retryTimer !== null) window.clearInterval(retryTimer)
    })
  })

  return { runSync }
}
